import {
  AppShellContent,
  Button,
  Chip,
  Group,
  Headline,
  Icon,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { AssetWorkOrderModal } from "../components/AssetWorkOrderModal";
import { AssetDetailFields } from "../components/assets/AssetDetailFields";
import {
  type AssetActivityEvent,
  buildAssetActivity,
  formatActivityTime,
} from "../components/assets/assetActivity";
import { CopyAssetLinkButton } from "../components/assets/CopyAssetLinkButton";
import { AssetDetailBack } from "../components/navigation/AssetDetailBack";
import {
  ASSET_KIND_LABEL,
  findParentRow,
  findPhysicalLocation,
  flattenAssetTree,
} from "../components/snowflakes/assets/assetModel";
import { NewWorkRequestSideSheet } from "../components/snowflakes/requests/NewWorkRequestSideSheet";
import { requestStatusTone } from "../components/snowflakes/requests/RequestQueue";
import {
  displayWorkOrderNumber,
  workOrderStatusTone,
} from "../components/snowflakes/work-orders/presentation";
import { DetailCard } from "../components/snowflakes/work-orders/WorkOrderDetailLayout";
import { useWorkScope } from "../components/WorkScopeProvider";
import { canAccessModule, canPerformAction } from "../lib/access";
import type { AssetTreeSearch } from "../lib/assetTreeSearch";
import { canStartWorkOrderFromAsset } from "../lib/assetWorkOrders";
import { type NavigationOrigin, toOriginSearch } from "../lib/navigationOrigin";
import type { AssetId, WorkOrderId, WorkRequestId } from "../lib/workIds";
import { getWorkScopeSearch } from "../lib/workSearch";
import { useAssetTreeQuery } from "../queries/useAssetTreeQuery";
import { useAssetWorkOrderCreation } from "../queries/useAssetWorkOrderCreation";
import { useAssetWorkRequestsQuery } from "../queries/useAssetWorkRequestsQuery";
import { useAssetWorkOrderHistoryQuery } from "../queries/useWorkOrdersQuery";

type AssetDetailTab = "overview" | "analysis" | "activity";

function ActivityCard({
  event,
  onOpen,
}: {
  event: AssetActivityEvent;
  onOpen: () => void;
}) {
  const time = formatActivityTime(event.at);
  return (
    <div className="rounded-(--radius-md) border border-outline-subtle bg-surface px-(--spacing-16) py-(--spacing-12)">
      <div className="flex items-center justify-between gap-(--spacing-12)">
        <Text size="sm" tone="subtle">
          {time.relative}
        </Text>
        <Text size="sm" tone="subtle">
          {time.absolute}
        </Text>
      </div>
      <div className="mt-(--spacing-8) flex items-center gap-(--spacing-8)">
        <Icon
          name={event.target.kind === "WORK_ORDER" ? "edit-pen" : "new-message"}
          size="sm"
          tone="secondary"
        />
        <Text as="span" size="md">
          {event.action}:
        </Text>
        <button
          className="min-w-0 truncate rounded-(--radius-xs) text-left font-(family-name:--font-family-body) text-(length:--font-size-body-md) text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-button-focus-ring"
          onClick={onOpen}
          type="button"
        >
          <span className="tabular-nums">{event.reference}</span> ·{" "}
          {event.title}
        </button>
      </div>
      <div className="mt-(--spacing-8) flex items-center gap-(--spacing-8)">
        <Chip
          label={event.target.kind === "WORK_ORDER" ? "Work Order" : "Request"}
          tone="neutral"
        />
        {event.actor ? (
          <span className="ml-auto">
            <Text as="span" size="sm" tone="subtle">
              {event.actor}
            </Text>
          </span>
        ) : null}
      </div>
    </div>
  );
}

const EYEBROW_LABEL_CLASS =
  "font-(number:--font-weight-semibold) text-(length:--font-size-label-small) uppercase tracking-(--font-letter-spacing-default) text-text-subtle tabular-nums";

const PANEL_ROW_CLASS =
  "flex w-full items-center gap-(--spacing-12) px-(--spacing-16) py-(--spacing-12) text-left outline-none hover:bg-surface-hover focus-visible:bg-surface-hover";

function PanelMessage({
  children,
  tone = "subtle",
}: {
  children: string;
  tone?: "subtle" | "negative";
}) {
  return (
    <div className="px-(--spacing-16) py-(--spacing-16)">
      <Text size="sm" tone={tone}>
        {children}
      </Text>
    </div>
  );
}

function PanelRow({
  number,
  title,
  status,
  onOpen,
}: {
  number: string;
  title: string;
  status: ReactNode;
  onOpen: () => void;
}) {
  return (
    <button className={PANEL_ROW_CLASS} onClick={onOpen} type="button">
      <span className="shrink-0 font-(number:--font-weight-semibold) text-(length:--font-size-body-sm) text-primary tabular-nums">
        {number}
      </span>
      <span className="min-w-0 flex-1">
        <Text as="span" size="sm" truncate>
          {title}
        </Text>
      </span>
      {status}
    </button>
  );
}

export function AssetDetailScreen({
  assetTreeSearch,
  assetId: assetIdParam,
  origin,
}: {
  assetTreeSearch?: AssetTreeSearch;
  assetId: AssetId;
  /** The record in another module this page was opened from, if any. */
  origin?: NavigationOrigin;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const navigate = useNavigate();
  const { createWorkOrder, attachProcedure } = useAssetWorkOrderCreation();
  const [activeTab, setActiveTab] = useState<AssetDetailTab>("overview");
  const [isNewWorkOrderOpen, setIsNewWorkOrderOpen] = useState(false);
  const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);
  const assetTreeQuery = useAssetTreeQuery();
  const allRows = flattenAssetTree(assetTreeQuery.data?.nodes ?? []);
  const treeAsset = allRows.find((row) => row.assetId === assetIdParam);
  const asset = treeAsset
    ? {
        code: treeAsset.code,
        assetId: treeAsset.assetId,
        id: treeAsset.id,
        kind: treeAsset.kind,
        sapFunctionalLocationId: treeAsset.sapFunctionalLocationId,
        manufacturer: treeAsset.manufacturer,
        model: treeAsset.model,
        name: treeAsset.name,
      }
    : null;
  const physicalLocationRow = treeAsset
    ? findPhysicalLocation(allRows, treeAsset)
    : null;
  const parentAssetRow = treeAsset ? findParentRow(allRows, treeAsset) : null;
  const canCreateWorkOrder =
    Boolean(asset?.assetId) &&
    canStartWorkOrderFromAsset(asset?.kind, selectedFacility);
  const canCreateRequest = Boolean(
    selectedFacility && canPerformAction(selectedFacility, "CREATE_REQUEST"),
  );
  const canViewWorkOrders = Boolean(
    selectedFacility && canAccessModule(selectedFacility, "WORK_ORDERS"),
  );
  const canViewRequests = Boolean(
    selectedFacility && canAccessModule(selectedFacility, "REQUESTS"),
  );
  const historyQuery = useAssetWorkOrderHistoryQuery(
    treeAsset?.assetId ?? null,
  );
  const requestsQuery = useAssetWorkRequestsQuery(
    canViewRequests ? (treeAsset?.assetId ?? null) : null,
  );
  const scopeSearch =
    selectedCustomer && selectedFacility
      ? getWorkScopeSearch(selectedCustomer, selectedFacility)
      : {};

  function openWorkOrder(workOrderId: WorkOrderId) {
    void navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: {
        ...scopeSearch,
        workOrderFullPage: true,
        ...(asset?.assetId
          ? toOriginSearch(
              {
                type: "asset",
                id: asset.assetId,
                search: assetTreeSearch,
                parent: origin,
              },
              { type: "workOrder", id: workOrderId },
            )
          : {}),
      },
    });
  }

  function openWorkRequest(workRequestId: WorkRequestId) {
    void navigate({
      to: "/requests/$workRequestId",
      params: { workRequestId },
      search: scopeSearch,
    });
  }

  if (!asset && assetTreeQuery.isPending) {
    return (
      <AppShellContent pending>
        <span />
      </AppShellContent>
    );
  }

  if (!asset) {
    return (
      <AppShellContent>
        <Stack gap={16}>
          <Text tone="negative">
            {assetTreeQuery.isError
              ? "Unable to load this asset."
              : "This asset could not be found in the selected facility."}
          </Text>
          {assetTreeQuery.isError ? (
            <Button
              onClick={() => void assetTreeQuery.refetch()}
              size="sm"
              variant="outline"
            >
              Retry
            </Button>
          ) : null}
          <AssetDetailBack assetTreeSearch={assetTreeSearch} origin={origin} />
        </Stack>
      </AppShellContent>
    );
  }

  const workOrderCount = historyQuery.data?.totalCount ?? null;
  const requestCount = requestsQuery.data?.requests.length ?? null;
  // Only events from modules this role can open, so the feed never
  // discloses a Work Order or Request the role isn't permitted to see.
  const activity = buildAssetActivity(
    canViewWorkOrders ? (historyQuery.data?.workOrders ?? []) : [],
    canViewRequests ? (requestsQuery.data?.requests ?? []) : [],
  );

  return (
    <>
      <AppShellContent>
        <Stack gap={16}>
          <AssetDetailBack assetTreeSearch={assetTreeSearch} origin={origin} />

          <div className="flex flex-wrap items-end justify-between gap-(--spacing-16)">
            <div className="min-w-0">
              <div className="flex items-center gap-(--spacing-8)">
                <Headline as="h1" size="lg">
                  {asset.name}
                </Headline>
                <CopyAssetLinkButton resetKey={asset.assetId} />
              </div>
              <div className="mt-(--spacing-8) flex flex-wrap items-center gap-(--spacing-12)">
                <span className="font-(number:--font-weight-semibold) text-(length:--font-size-label-medium) uppercase tracking-(--font-letter-spacing-default) text-primary tabular-nums">
                  {asset.code}
                </span>
                {asset.kind ? (
                  <Chip label={ASSET_KIND_LABEL[asset.kind]} tone="neutral" />
                ) : null}
              </div>
            </div>
            <Group>
              {canCreateRequest ? (
                <Button
                  icon="plus-regular"
                  onClick={() => setIsNewRequestOpen(true)}
                  size="sm"
                  variant="outline"
                >
                  New Request
                </Button>
              ) : null}
              {canCreateWorkOrder ? (
                <Button
                  icon="plus-regular"
                  onClick={() => setIsNewWorkOrderOpen(true)}
                  size="sm"
                >
                  New Work Order
                </Button>
              ) : null}
            </Group>
          </div>

          <Tabs
            onValueChange={(value) => setActiveTab(value as AssetDetailTab)}
            value={activeTab}
          >
            <TabsList aria-label="Asset details">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="analysis">Analysis</TabsTrigger>
              <TabsTrigger value="activity">Recent Activity</TabsTrigger>
            </TabsList>
          </Tabs>

          {activeTab === "overview" ? (
            <Stack gap={16}>
              <DetailCard title="Details">
                <div className="px-(--spacing-24) py-(--spacing-20)">
                  <AssetDetailFields
                    asset={asset}
                    // The POC Details card has no hierarchy line; Physical
                    // Location and Parent Asset ID already place the record.
                    hierarchyPath={[]}
                    layout="grid"
                    parentAsset={
                      parentAssetRow
                        ? {
                            code: parentAssetRow.code,
                            name: parentAssetRow.name,
                          }
                        : null
                    }
                    physicalLocation={
                      physicalLocationRow
                        ? {
                            name: physicalLocationRow.name,
                            code: physicalLocationRow.code,
                          }
                        : null
                    }
                  />
                </div>
              </DetailCard>
            </Stack>
          ) : activeTab === "analysis" ? (
            <Stack gap={16}>
              <div className="rounded-(--radius-md) border border-outline-subtle bg-surface px-(--spacing-24) py-(--spacing-20)">
                <Text weight="semibold">
                  Real-data record — no condition-monitoring telemetry connected
                </Text>
                <Text size="sm" tone="subtle">
                  See the Details card on the Overview tab for asset fields.
                </Text>
              </div>
              <div className="grid gap-(--spacing-16) md:grid-cols-2">
                <DetailCard
                  actions={
                    canViewWorkOrders && workOrderCount !== null ? (
                      <span className={EYEBROW_LABEL_CLASS}>
                        {workOrderCount} on this asset
                      </span>
                    ) : null
                  }
                  title="Work orders"
                >
                  <div className="max-h-[360px] overflow-y-auto">
                    {!canViewWorkOrders ? (
                      <PanelMessage>
                        Work Orders are unavailable for your role.
                      </PanelMessage>
                    ) : historyQuery.isPending ? (
                      <PanelMessage>Loading work orders…</PanelMessage>
                    ) : historyQuery.isError ? (
                      <PanelMessage tone="negative">
                        Unable to load work orders.
                      </PanelMessage>
                    ) : historyQuery.data?.workOrders.length ? (
                      <div className="divide-y divide-outline-subtle">
                        {historyQuery.data.workOrders.map((workOrder) => (
                          <PanelRow
                            key={workOrder.id}
                            number={displayWorkOrderNumber(workOrder.number)}
                            onOpen={() => openWorkOrder(workOrder.id)}
                            status={
                              <Chip
                                label={workOrder.status.name}
                                tone={
                                  workOrderStatusTone[workOrder.status.code] ??
                                  "neutral"
                                }
                              />
                            }
                            title={workOrder.title}
                          />
                        ))}
                      </div>
                    ) : (
                      <PanelMessage>No work orders on this asset.</PanelMessage>
                    )}
                  </div>
                </DetailCard>
                <DetailCard
                  actions={
                    canViewRequests && requestCount !== null ? (
                      <span className={EYEBROW_LABEL_CLASS}>
                        {requestCount} on this asset
                      </span>
                    ) : null
                  }
                  title="Work requests"
                >
                  <div className="max-h-[360px] overflow-y-auto">
                    {!canViewRequests ? (
                      <PanelMessage>
                        Work Requests are unavailable for your role.
                      </PanelMessage>
                    ) : requestsQuery.isPending ? (
                      <PanelMessage>Loading work requests…</PanelMessage>
                    ) : requestsQuery.isError ? (
                      <PanelMessage tone="negative">
                        Unable to load work requests.
                      </PanelMessage>
                    ) : requestsQuery.data?.requests.length ? (
                      <div className="divide-y divide-outline-subtle">
                        {requestsQuery.data.requests.map((request) => (
                          <PanelRow
                            key={request.id}
                            number={request.requestNumber}
                            onOpen={() => openWorkRequest(request.id)}
                            status={
                              <Chip
                                label={request.status.name}
                                tone={requestStatusTone(request.status.code)}
                              />
                            }
                            title={request.title}
                          />
                        ))}
                      </div>
                    ) : (
                      <PanelMessage>
                        No work requests on this asset.
                      </PanelMessage>
                    )}
                  </div>
                </DetailCard>
              </div>
            </Stack>
          ) : !canViewWorkOrders && !canViewRequests ? (
            <Text tone="subtle">
              Recent activity is unavailable for your role.
            </Text>
          ) : historyQuery.isPending && requestsQuery.isPending ? (
            <Text tone="subtle">Loading recent activity…</Text>
          ) : activity.length ? (
            <Stack gap={12}>
              {activity.map((event) => (
                <ActivityCard
                  event={event}
                  key={event.key}
                  onOpen={() =>
                    event.target.kind === "WORK_ORDER"
                      ? openWorkOrder(event.target.id)
                      : openWorkRequest(event.target.id)
                  }
                />
              ))}
            </Stack>
          ) : (
            <Text tone="subtle">No recent activity on this asset.</Text>
          )}
        </Stack>
      </AppShellContent>
      <AssetWorkOrderModal
        asset={{
          id: asset.id,
          assetId: asset.assetId,
          name: asset.name,
          locationName:
            physicalLocationRow?.name ?? treeAsset?.locationCode ?? null,
        }}
        onAttachProcedure={attachProcedure}
        onCreate={createWorkOrder}
        onCreated={openWorkOrder}
        onOpenChange={setIsNewWorkOrderOpen}
        open={isNewWorkOrderOpen}
      />
      <NewWorkRequestSideSheet
        // Remounts on asset change so a request draft never carries a stale
        // asset after navigating to a different record on the same route.
        initialAssetId={asset.assetId}
        initialAssetName={asset.name}
        key={asset.assetId ?? "none"}
        onOpenChange={setIsNewRequestOpen}
        onViewRequests={openWorkRequest}
        open={isNewRequestOpen}
      />
    </>
  );
}
